import React, { useState } from 'react';
import {
  Card, CardHeader, Divider, CardContent, Grid, Box, Typography,
  Button, Collapse, Alert,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PublishIcon from '@mui/icons-material/Publish';
import JobStatusIndicator from '../../../features/patches/components/JobStatusIndicator';
import { MenuViewer } from '../Menu';
import { Store } from '../../../types';

interface Step4PublishAndReviewProps {
  jobStatus: any;
  menuJobStatus: any;
  isMenuJobPolling: boolean;
  selectedStores: Store[];
  submitError: string | null;
  isSubmitting: boolean;
  success: boolean;
  publishConfirmed: boolean;
  onPublishAndVerify: () => void;
}

const Step4PublishAndReview: React.FC<Step4PublishAndReviewProps> = ({
  jobStatus,
  menuJobStatus,
  isMenuJobPolling,
  selectedStores,
  submitError,
  isSubmitting,
  success,
  publishConfirmed,
  onPublishAndVerify,
}) => {
  const [publishExpanded, setPublishExpanded] = useState(false);

  const patchCompleted = jobStatus?.status === 'COMPLETED';
  const patchFailed = jobStatus?.status === 'FAILED';
  const menuCompleted = !isMenuJobPolling && menuJobStatus?.status === 'COMPLETED';

  return (
    <Card>
      <CardHeader title='Patch Results & Publish' />
      <Divider />
      <CardContent>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            {/* --- Section 1: Patch job outcome (always visible, primary focus) --- */}
            <Box sx={{ mb: 3 }}>
              <Typography variant='h6' gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                Patch Changes
                {patchCompleted && (
                  <Typography color='success.main' component='span'>✓</Typography>
                )}
                {patchFailed && (
                  <Typography color='error.main' component='span'>✗</Typography>
                )}
              </Typography>
              <JobStatusIndicator
                status={jobStatus?.status}
                progress={jobStatus?.progress}
                total={jobStatus?.total}
                error={submitError}
                isSubmitting={isSubmitting}
                success={success}
                type="patch"
                totalSubJobs={jobStatus?.totalSubJobs}
                totalSubJobsCompleted={jobStatus?.totalSubJobsCompleted}
                totalSubJobsFailed={jobStatus?.totalSubJobsFailed}
                totalSubJobsInProgress={jobStatus?.totalSubJobsInProgress}
                startedAt={jobStatus?.startedAt}
                completedAt={jobStatus?.completedAt}
              />
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* --- Section 2: Publish & Verify (collapsed, requires intentional action) --- */}
            <Box sx={{ mb: 3 }}>
              {!publishConfirmed ? (
                <>
                  <Button
                    variant="outlined"
                    disabled={!patchCompleted || patchFailed}
                    onClick={() => setPublishExpanded((prev) => !prev)}
                    endIcon={
                      <ExpandMoreIcon
                        sx={{
                          transform: publishExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.2s',
                        }}
                      />
                    }
                    sx={{ mb: 1 }}
                  >
                    Publish &amp; Verify
                  </Button>

                  <Collapse in={publishExpanded}>
                    <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'warning.light', borderRadius: 1, bgcolor: 'warning.50' }}>
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        This will publish ECOMM menus to <strong>{selectedStores.length}</strong> store{selectedStores.length !== 1 ? 's' : ''}.
                        The publish job may take several minutes to complete.
                      </Alert>
                      <Button
                        variant="contained"
                        color="warning"
                        startIcon={<PublishIcon />}
                        onClick={onPublishAndVerify}
                        disabled={isMenuJobPolling}
                      >
                        Confirm Publish
                      </Button>
                    </Box>
                  </Collapse>
                </>
              ) : (
                <>
                  <Typography variant='h6' gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    Menu Publish
                    {menuCompleted && (
                      <Typography color='success.main' component='span'>✓</Typography>
                    )}
                  </Typography>
                  {menuJobStatus ? (
                    <JobStatusIndicator
                      status={menuJobStatus.status}
                      progress={menuJobStatus.progress}
                      total={menuJobStatus.total}
                      type="menu"
                      totalSubJobs={menuJobStatus.totalSubJobs}
                      totalSubJobsCompleted={menuJobStatus.totalSubJobsCompleted}
                      totalSubJobsFailed={menuJobStatus.totalSubJobsFailed}
                      totalSubJobsInProgress={menuJobStatus.totalSubJobsInProgress}
                      startedAt={menuJobStatus.startedAt}
                      completedAt={menuJobStatus.completedAt}
                    />
                  ) : (
                    <Typography color='text.secondary'>
                      Waiting for menu publishing to start…
                    </Typography>
                  )}
                </>
              )}
            </Box>

            {/* --- Section 3: Menu verification (only after publish completes) --- */}
            {publishConfirmed && menuCompleted && (
              <Box sx={{ mb: 3, pr: 3 }}>
                <Divider sx={{ mb: 3 }} />
                <Typography variant='h6' gutterBottom>
                  Review Store Menus
                </Typography>
                <MenuViewer selectedStores={selectedStores} verifyMode />
              </Box>
            )}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default Step4PublishAndReview;
